import type React from "react";
import type { Book } from "../../../app/books";
import type { BooksSearchSchema } from "./search";

import { Link, useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import { useBooks } from "../../../app/books";

export const Results: React.FC = () => {
  const search: BooksSearchSchema = useSearch({ from: "/books" });
  const books = useBooks(search);

  return (
    <div className="flex flex-col gap-4">
      {books.data.map((book) => (
        <BookLine key={book.id} book={book} />
      ))}
    </div>
  );
};

type BookProps = {
  book: Book;
};

const BookLine: React.FC<BookProps> = ({ book }) => {
  const { t } = useTranslation();

  return (
    <div className="dui-card dui-card-side bg-base-100 overflow-hidden shadow-xl">
      <figure className="w-32 flex-shrink-0 self-start">
        <Link to="/books/$id" params={{ id: book.id }}>
          <img alt={book.title} className="h-auto w-full object-contain" src={book.cover ?? "/nocover.jpg"} />
        </Link>
      </figure>

      <div className="dui-card-body py-4">
        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-xl font-bold">{book.title}</h2>
            <p className="flex flex-wrap gap-3 text-sm text-gray-500">
              {book.author.map((author) => (
                <Link to="/books" key={author.id} search={{ q: "author:" + author.name }}>
                  {author.name}
                </Link>
              ))}
            </p>
          </div>
          <div className="my-1 mb-1 flex flex-wrap gap-1">
            <div className="dui-badge dui-badge-success dui-badge-sm">{t(`books.badges.read.${book.read}`)}</div>
            {book.tbr === true && (
              <div className="dui-badge dui-badge-outline dui-badge-sm">{t("books.badges.tbr.true")}</div>
            )}
            {book.owned !== undefined && (
              <div className="dui-badge dui-badge-outline dui-badge-sm">{t(`books.badges.owned.${book.owned}`)}</div>
            )}
          </div>
        </div>

        <p className="hidden text-sm md:block">
          <span className="line-clamp-4">{book.description}</span>
        </p>

        {(book.series || book.universe) && (
          <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex max-w-full flex-wrap gap-2 overflow-hidden">
              {book.series?.map((series, index) => (
                <Link
                  to="/books"
                  key={index}
                  search={{ q: "series:" + series.name }}
                  className="dui-badge dui-badge-primary dui-badge-lg whitespace-nowrap"
                >
                  {t("book.maininfo.series", {
                    ...series,
                    context: series.position ? "withPosition" : undefined
                  })}
                </Link>
              ))}
              {book.universe?.map((universe, index) => (
                <Link
                  to="/books"
                  key={index}
                  search={{ q: "universe:" + universe }}
                  className="dui-badge dui-badge-secondary dui-badge-lg whitespace-nowrap"
                >
                  {universe}
                </Link>
              ))}
            </div>
            <div className="dui-card-actions sm:justify-end">
              <Link to="/books/$id" params={{ id: book.id }} className="dui-btn dui-btn-sm dui-btn-outline">
                {t("books.actions.details")}
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
